import IconLoading from '@/icons/loading-dots.svg?raw'
import type { Product } from '@/lib/types/Product'
import * as CartService from '../services/cart'

type ProductPair = {
  starter: Product
  ultimate: Product | null
}

const refreshProductCarousel = (root: HTMLElement, index: string) => {
  const carousel = root.querySelector(
    `[data-splide-carousel][data-product-index="${index}"]`,
  ) as (HTMLElement & { splide?: { refresh: () => void } }) | null

  carousel?.splide?.refresh()
}

const resetKitTier = (root: HTMLElement) => {
  root.dataset.selectedKitTier = 'starter'
  const kitPairs = root.querySelectorAll('[data-kit-tier-option]')
  kitPairs.forEach((option) => option.removeAttribute('data-selected'))
  const activePair = root.querySelector(
    `.StarterKitMultistep__KitPair[data-product-index="${root.dataset.selectedProductIndex}"]`,
  )
  activePair
    ?.querySelector('[data-kit-tier-option="starter"]')
    ?.setAttribute('data-selected', '')
}

const getSelectedKitOption = (root: HTMLElement) => {
  const scentIndex = root.dataset.selectedProductIndex ?? '0'
  const kitTier = root.dataset.selectedKitTier ?? 'starter'
  const activePair = root.querySelector(
    `.StarterKitMultistep__KitPair[data-product-index="${scentIndex}"]`,
  )

  return activePair?.querySelector(
    `[data-kit-tier-option="${kitTier}"]`,
  ) as HTMLElement | null
}

const updateMultistepFooter = (root: HTMLElement) => {
  const selectedOption = getSelectedKitOption(root)
  const kitTier = root.dataset.selectedKitTier ?? 'starter'

  const footerLabel = root.querySelector('[data-starter-kit-footer-label]')
  const footerPrice = root.querySelector('[data-starter-kit-footer-price]')
  const footerCompare = root.querySelector('[data-starter-kit-footer-compare]')

  if (footerLabel) {
    footerLabel.textContent =
      kitTier === 'ultimate' ? 'ULTIMATE KIT • TODAY' : 'STARTER KIT • TODAY'
  }

  if (footerPrice && selectedOption?.dataset.priceFormatted) {
    footerPrice.textContent = selectedOption.dataset.priceFormatted
  }

  if (footerCompare) {
    const compareFormatted = selectedOption?.dataset.compareFormatted
    const priceFormatted = selectedOption?.dataset.priceFormatted
    if (
      compareFormatted &&
      priceFormatted &&
      compareFormatted !== priceFormatted
    ) {
      footerCompare.textContent = compareFormatted
      footerCompare.removeAttribute('hidden')
    } else {
      footerCompare.textContent = ''
      footerCompare.setAttribute('hidden', '')
    }
  }
}

const initFragranceSwitcher = (
  root: HTMLElement,
  purchaseButton: HTMLButtonElement,
) => {
  const fragranceOptions = root.querySelectorAll(
    '[data-fragrance-option-index]',
  )

  fragranceOptions?.forEach((option) => {
    option.addEventListener('click', () => {
      if (option.getAttribute('data-available') === 'false') return

      const index = (option as HTMLElement).dataset.fragranceOptionIndex
      if (index === undefined) return

      fragranceOptions.forEach((o) => {
        if ((o as HTMLElement).dataset.fragranceOptionIndex === index) {
          o.setAttribute('data-selected', '')
        } else {
          o.removeAttribute('data-selected')
        }
      })

      root.dataset.selectedProductIndex = index
      purchaseButton.dataset.selectedProductIndex = index

      refreshProductCarousel(root, index)
      resetKitTier(root)
      updateMultistepFooter(root)
    })
  })
}

const initMultistepSection = (
  root: HTMLElement,
  purchaseButton: HTMLButtonElement,
  buttonIndex: number,
) => {
  initFragranceSwitcher(root, purchaseButton)

  root
    .querySelector('[data-starter-kit-advance-step]')
    ?.addEventListener('click', () => {
      root.dataset.currentStep = '2'
      window.mixpanel.track('starter_kit_lp_step_advance', {
        step: 2,
        product_index: root.dataset.selectedProductIndex,
      })
      root.scrollIntoView({ behavior: 'smooth', block: 'start' })
      updateMultistepFooter(root)
    })

  root
    .querySelector('[data-starter-kit-back-step]')
    ?.addEventListener('click', () => {
      root.dataset.currentStep = '1'
      root.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })

  root.querySelectorAll('[data-kit-tier-option]').forEach((option) => {
    option.addEventListener('click', () => {
      const kitPair = option.closest('.StarterKitMultistep__KitPair')
      kitPair
        ?.querySelectorAll('[data-kit-tier-option]')
        .forEach((o) => o.removeAttribute('data-selected'))
      option.setAttribute('data-selected', '')
      const tier = (option as HTMLElement).dataset.kitTierOption ?? 'starter'
      root.dataset.selectedKitTier = tier
      window.mixpanel.track('starter_kit_lp_kit_tier_select', {
        kit_tier: tier,
        product_index: root.dataset.selectedProductIndex,
      })
      updateMultistepFooter(root)
    })
  })

  const productJson = JSON.parse(
    purchaseButton.dataset.productJson ?? '[]',
  ) as ProductPair[]

  purchaseButton.addEventListener('click', async () => {
    const scentIndex = Number(root.dataset.selectedProductIndex) ?? 0
    const kitTier = root.dataset.selectedKitTier ?? 'starter'
    const pair = productJson[scentIndex]
    const product =
      kitTier === 'ultimate' && pair?.ultimate ? pair.ultimate : pair?.starter

    if (!product) return

    window.mixpanel.track('starter_kit_lp_purchase_click', {
      product,
      button_index: buttonIndex,
      kit_tier: kitTier,
      product_index: scentIndex,
    })

    purchaseButton.classList.add('Button--loading')
    purchaseButton.classList.add('Button--is-disabled')
    purchaseButton.setAttribute('disabled', 'disabled')
    const loading = document.createElement('span')
    loading.classList.add('Button__Icon')
    loading.classList.add('Button__Icon--loading')
    loading.innerHTML = IconLoading
    purchaseButton.textContent = ''
    purchaseButton.appendChild(loading)

    await CartService.add({
      id: product.id,
      selectedVariantId: product.selectedVariantId.toString(),
      selectedSellingPlanId:
        product.variants[
          product.selectedVariantId
        ].sellingPlanAllocations[0]?.sellingPlan.id.toString(),
      quantity: 1,
    })

    CartService.checkout()
  })
}

export default function init() {
  document
    .querySelectorAll('[data-starter-kit-multistep]')
    .forEach((rootEl, i) => {
      const root = rootEl as HTMLElement
      const purchaseButton = root.querySelector(
        '[data-starter-kit-multistep-purchase]',
      ) as HTMLButtonElement | null

      if (!purchaseButton?.dataset?.productJson) return

      initMultistepSection(root, purchaseButton, i)
    })
}
